import { Component, inject, input, signal } from '@angular/core';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCol,
  IonGrid,
  IonRow,
  ModalController,
  NavController
} from '@ionic/angular/standalone';
import { CustomModalComponent } from '@shared/components/modals/custom-modal/custom-modal.component';
import type { ActionButton } from '@shared/components/modals/custom-modal/models/action-button.models';
import { ActionButtonTypes } from '@shared/components/modals/custom-modal/models/action-button.enum';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { AuthService } from '@core/auth/services/auth.service';
import { PersonHttpService } from '@features/person/services/person-http.service';
import { LoadingService } from '@core/services/loading.service';

@Component({
  selector: 'app-privacy-policy',
  templateUrl: './privacy-policy.component.html',
  styleUrls: ['./privacy-policy.component.scss'],
  standalone: true,
  imports: [
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonCol,
    IonGrid,
    IonRow,
    CustomModalComponent
  ]
})
export class PrivacyPolicyComponent {
  private modalController = inject(ModalController);
  private authService = inject(AuthService);
  private navCtrl = inject(NavController);
  private personHttpService = inject(PersonHttpService);
  private loadingService = inject(LoadingService);

  hideActionButtons = input(false);

  title = 'Privacy Policy'.toUpperCase();

  footerClass = signal('');
  actionButtons = signal<ActionButton[]>([
    { text: 'Disagree', action: ActionButtonTypes.decline, color: 'secondary' }
  ]);

  handleBack(): void {
    this.modalController.dismiss({ dismissed: 'back' }).then();
  }

  async handleAction(action: string): Promise<void> {
    if (action === ActionButtonTypes.decline) {
      await this.openDeclineConfirmationModal();
    } else if (action === ActionButtonTypes.accept) {
      this.modalController.dismiss({ action: 'accepted' });
    }
  }

  async openDeclineConfirmationModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: ConfirmationModalComponent,
      componentProps: {
        title: signal('Decline Privacy Policy'),
        content:
          signal(`<p>By declining the privacy policy, you will not be able to use this application.</p>
                  <p>You will be logged out and returned to the login screen.</p>
                  <p>Are you sure you want to continue?</p>`),
        yesText: signal('Yes, Decline'),
        noText: signal('Cancel'),
        showCloseButton: signal(false)
      },
      backdropDismiss: false,
      cssClass: 'centered-dialog-modal'
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data) {
      this.loadingService.startLoading();
      this.personHttpService.revokePrivacyPolicyDate().subscribe(() => {
        this.modalController
          .dismiss({ action: 'declined_confirmed' })
          .then(() => {
            this.loadingService.stopLoading();
            this.goToLogout();
          })
          .catch(() => {
            this.loadingService.stopLoading();
            this.goToLogout();
          });
      });
    }
  }

  private async goToLogout() {
    await this.authService.logout();
    // Reload the page to reset environment to build configuration
    window.location.href = '/auth';
  }
}
